import { Head, useForm } from "@inertiajs/react"
import { Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import LocationSelect from "@/components/location-select"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import type RequestsCreateTryout from "@/types/serializers/RequestsCreateTryout"

interface CategoryData {
  birth_year_from: number
  birth_year_to: number
  date: string
  start_time: string
  end_time: string
  max_spots: number | null
}

export default function TryoutNew() {
  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [showNoCategories, setShowNoCategories] = useState(false)
  const [newCategory, setNewCategory] = useState<CategoryData>({
    birth_year_from: 2010,
    birth_year_to: 2012,
    date: "",
    start_time: "09:00",
    end_time: "12:00",
    max_spots: null,
  })

  const { data, setData, post, processing, errors, clearErrors } =
    useForm<RequestsCreateTryout>({
      tryout: {
        title: "",
        description: "",
        venue_name: "",
        address: null,
        city: null,
        region: null,
        country: "",
        free: true,
        requirements: null,
        contact_phone: null,
        contact_email: null,
      },
    })

  const setField = (
    field: keyof typeof data.tryout,
    value: string | boolean | null,
  ) =>
    setData("tryout", {
      ...data.tryout,
      [field]: value,
    } as typeof data.tryout)

  function handleAddCategory() {
    if (
      !newCategory.date ||
      !newCategory.birth_year_from ||
      !newCategory.birth_year_to
    ) {
      return
    }
    const currentCategories = data.tryout.tryout_categories_attributes ?? []
    setData("tryout", {
      ...data.tryout,
      tryout_categories_attributes: [...currentCategories, newCategory],
    } as typeof data.tryout)
    setNewCategory({
      birth_year_from: 2010,
      birth_year_to: 2012,
      date: "",
      start_time: "09:00",
      end_time: "12:00",
      max_spots: null,
    })
    setShowCategoryForm(false)
    setShowNoCategories(false)
    clearErrors()
  }

  function handleRemoveCategory(index: number) {
    const currentCategories = data.tryout.tryout_categories_attributes ?? []
    setData("tryout", {
      ...data.tryout,
      tryout_categories_attributes: currentCategories.filter(
        (_, i) => i !== index,
      ),
    } as typeof data.tryout)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const categories = data.tryout.tryout_categories_attributes ?? []
    if (categories.length === 0 && !showNoCategories) {
      setShowNoCategories(true)
      return
    }
    post("/pruebas")
  }

  return (
    <AppLayout>
      <Head title="Crear prueba | KrownFT" />

      <div className="min-h-svh">
        <div className="mx-auto max-w-2xl px-4 py-6">
          <h1 className="mb-6 text-2xl font-bold">Crear prueba</h1>

          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="title">Título</Label>
                  <Input
                    id="title"
                    value={data.tryout.title}
                    onChange={(e) => setField("title", e.target.value)}
                    placeholder="Ej: Prueba abierta Sub-15 2026"
                  />
                  {errors["tryout.title"] && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors["tryout.title"]}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={data.tryout.description}
                    onChange={(e) => setField("description", e.target.value)}
                    placeholder="Describe la prueba, qué se busca, y cualquier detalle relevante..."
                    rows={5}
                  />
                  {errors["tryout.description"] && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors["tryout.description"]}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="venue_name">Nombre del predio</Label>
                  <Input
                    id="venue_name"
                    value={data.tryout.venue_name}
                    onChange={(e) => setField("venue_name", e.target.value)}
                    placeholder="Ej: Complejo Deportivo Municipal"
                  />
                  {errors["tryout.venue_name"] && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors["tryout.venue_name"]}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="address">Dirección</Label>
                  <Input
                    id="address"
                    value={data.tryout.address ?? ""}
                    onChange={(e) =>
                      setField("address", e.target.value || null)
                    }
                    placeholder="Ej: Av. del Libertador 1234"
                  />
                </div>

                <LocationSelect
                  country={data.tryout.country}
                  region={data.tryout.region ?? ""}
                  city={data.tryout.city ?? ""}
                  onCountryChange={(v) => setField("country", v)}
                  onRegionChange={(v) => setField("region", v || null)}
                  onCityChange={(v) => setField("city", v || null)}
                  showCity={true}
                  errors={{
                    country: errors["tryout.country"] as string | undefined,
                  }}
                />

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="free"
                    checked={data.tryout.free}
                    onCheckedChange={(checked) =>
                      setField("free", checked === true)
                    }
                  />
                  <Label htmlFor="free" className="cursor-pointer">
                    La prueba es gratuita
                  </Label>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="requirements">
                    Requisitos (qué traer, vestimenta, etc.)
                  </Label>
                  <Textarea
                    id="requirements"
                    value={data.tryout.requirements ?? ""}
                    onChange={(e) =>
                      setField("requirements", e.target.value || null)
                    }
                    placeholder="Ej: Traer ropa deportiva, botines, documento..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="contact_phone">Teléfono de contacto</Label>
                    <Input
                      id="contact_phone"
                      value={data.tryout.contact_phone ?? ""}
                      onChange={(e) =>
                        setField("contact_phone", e.target.value || null)
                      }
                      placeholder="+54 11 1234-5678"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact_email">Email de contacto</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={data.tryout.contact_email ?? ""}
                      onChange={(e) =>
                        setField("contact_email", e.target.value || null)
                      }
                      placeholder="pruebas@club.com"
                    />
                  </div>
                </div>

                {showNoCategories && (
                  <div className="rounded-lg border border-yellow-500 bg-yellow-500/10 p-3">
                    <p className="text-sm text-yellow-600">
                      Esta prueba no tiene categorías. Los jugadores no podrán
                      inscribirse hasta que agregues una.
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Button type="submit" size="sm" disabled={processing}>
                        Crear de todos modos
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setShowNoCategories(false)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                {!showNoCategories && (
                  <Button
                    type="submit"
                    disabled={processing}
                    className="w-full"
                  >
                    Crear prueba
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Categorías */}
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Categorías</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCategoryForm(!showCategoryForm)}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Agregar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {showCategoryForm && (
                <div className="mb-4 space-y-4 rounded-lg border p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="birth_year_from">Año desde</Label>
                      <Input
                        id="birth_year_from"
                        type="number"
                        value={newCategory.birth_year_from}
                        onChange={(e) =>
                          setNewCategory({
                            ...newCategory,
                            birth_year_from: parseInt(e.target.value),
                          })
                        }
                        min={1990}
                        max={2020}
                      />
                    </div>
                    <div>
                      <Label htmlFor="birth_year_to">Año hasta</Label>
                      <Input
                        id="birth_year_to"
                        type="number"
                        value={newCategory.birth_year_to}
                        onChange={(e) =>
                          setNewCategory({
                            ...newCategory,
                            birth_year_to: parseInt(e.target.value),
                          })
                        }
                        min={1990}
                        max={2020}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="date">Fecha</Label>
                    <Input
                      id="date"
                      type="date"
                      min={new Date().toISOString().split("T")[0]}
                      value={newCategory.date}
                      onChange={(e) =>
                        setNewCategory({ ...newCategory, date: e.target.value })
                      }
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_time">Hora inicio</Label>
                      <Input
                        id="start_time"
                        type="time"
                        value={newCategory.start_time}
                        onChange={(e) =>
                          setNewCategory({
                            ...newCategory,
                            start_time: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_time">Hora fin</Label>
                      <Input
                        id="end_time"
                        type="time"
                        value={newCategory.end_time}
                        onChange={(e) =>
                          setNewCategory({
                            ...newCategory,
                            end_time: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="max_spots">Cupos máximos (opcional)</Label>
                    <Input
                      id="max_spots"
                      type="number"
                      value={newCategory.max_spots ?? ""}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          max_spots: e.target.value
                            ? parseInt(e.target.value)
                            : null,
                        })
                      }
                      min={1}
                      placeholder="Ilimitado"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button type="button" onClick={handleAddCategory}>
                      Agregar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCategoryForm(false)}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {(data.tryout.tryout_categories_attributes?.length ?? 0) > 0 ? (
                <div className="space-y-3">
                  {data.tryout.tryout_categories_attributes?.map(
                    (cat, index) => (
                      <div
                        key={index}
                        className="flex items-start justify-between rounded-lg border p-3"
                      >
                        <div>
                          <p className="font-medium">
                            {cat.birth_year_from}-{cat.birth_year_to}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {new Date(cat.date).toLocaleDateString("es-AR")} -{" "}
                            {cat.start_time} a {cat.end_time}
                          </p>
                          {cat.max_spots && (
                            <p className="text-muted-foreground text-sm">
                              Cupos: {cat.max_spots}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-400"
                          onClick={() => handleRemoveCategory(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Agregá al menos una categoría con fecha futura para que los
                  jugadores puedan inscribirse.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
